import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, inr } from "../../lib/api";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "../../components/ui/sonner";

const STATUS_COLORS = {
  PENDING: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  PAID: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  FAILED: "bg-red-500/15 text-red-400 border-red-500/30",
  REFUNDED: "bg-sky-500/15 text-sky-400 border-sky-500/30",
  PARTIALLY_REFUNDED: "bg-sky-500/15 text-sky-300 border-sky-500/20",
};

const SERVICE_LABELS = { standard: "STANDARD", specialist: "SPECIALIST", super: "SUPER VIBE" };

export default function Payments() {
  const [payments, setPayments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [active, setActive] = useState(null);
  const [refundAmount, setRefundAmount] = useState("");
  const [refundReason, setRefundReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/admin/payments").then((res) => setPayments(res.data)).catch(() => toast.error("Could not load payments"));
    api.get("/admin/invoices").then((res) => setInvoices(res.data)).catch(() => {});
  };
  useEffect(load, []);

  const refund = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/payments/${active.payment_id}/refund`, {
        amount: refundAmount ? parseFloat(refundAmount) : null,
        reason: refundReason,
      });
      toast.success("Refund initiated");
      setActive(null);
      setRefundAmount("");
      setRefundReason("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail === "payments_not_configured" ? "Razorpay not configured" : "Refund failed");
    } finally {
      setBusy(false);
    }
  };

  const effStatus = (p) => p.refund_status || p.status;

  return (
    <div data-testid="admin-payments-page">
      <h1 className="font-heading text-3xl font-extrabold text-white">Payments & Invoices</h1>
      <p className="mt-2 text-sm text-slate-400">Every transaction with full FX and audit trail.</p>

      <div className="mt-8 rounded-xl border border-white/10 bg-navy-800 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-white/10 hover:bg-transparent">
              <TableHead className="text-slate-400">Customer</TableHead>
              <TableHead className="text-slate-400">Service</TableHead>
              <TableHead className="text-slate-400 hidden md:table-cell">Base INR</TableHead>
              <TableHead className="text-slate-400">Charged</TableHead>
              <TableHead className="text-slate-400 hidden lg:table-cell">FX rate</TableHead>
              <TableHead className="text-slate-400">Status</TableHead>
              <TableHead className="text-slate-400 hidden sm:table-cell">Date</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {payments.map((p) => (
              <TableRow key={p.payment_id} data-testid={`payment-row-${p.payment_id}`} onClick={() => setActive(p)} className="border-white/5 cursor-pointer hover:bg-white/5">
                <TableCell className="font-semibold text-white">
                  {p.customer?.name}
                  <span className="block text-[11px] font-normal text-slate-500">{p.customer?.company || p.customer?.email}</span>
                </TableCell>
                <TableCell className="text-slate-300 text-xs">{SERVICE_LABELS[p.service]} · {p.hours}h</TableCell>
                <TableCell className="text-slate-300 hidden md:table-cell">{inr(p.total_inr)}</TableCell>
                <TableCell className="text-slate-200 font-semibold">{p.currency_amount?.toLocaleString("en-IN")} {p.currency}</TableCell>
                <TableCell className="text-slate-500 text-xs hidden lg:table-cell font-mono">{p.currency !== "INR" ? `1 INR = ${p.fx_rate} ${p.currency}` : "—"}</TableCell>
                <TableCell><Badge className={`font-mono text-[9px] ${STATUS_COLORS[effStatus(p)] || ""}`}>{effStatus(p).replace(/_/g, " ")}</Badge></TableCell>
                <TableCell className="text-slate-500 text-xs hidden sm:table-cell">{(p.paid_at || p.created_at)?.slice(0, 10)}</TableCell>
              </TableRow>
            ))}
            {!payments.length && (
              <TableRow><TableCell colSpan={7} className="text-center text-slate-500 py-12">No payments yet.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <h2 className="mt-12 font-heading text-xl font-bold text-white">Invoices</h2>
      <div className="mt-5 rounded-xl border border-white/10 bg-navy-800 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-white/10 hover:bg-transparent">
              <TableHead className="text-slate-400">Invoice #</TableHead>
              <TableHead className="text-slate-400">Customer</TableHead>
              <TableHead className="text-slate-400">Total INR</TableHead>
              <TableHead className="text-slate-400 hidden md:table-cell">Currency</TableHead>
              <TableHead className="text-slate-400 hidden sm:table-cell">Issued</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {invoices.map((inv) => (
              <TableRow key={inv.invoice_id} data-testid={`invoice-row-${inv.invoice_number}`} className="border-white/5">
                <TableCell className="font-mono text-flame">{inv.invoice_number}</TableCell>
                <TableCell className="text-slate-200">{inv.customer?.name}</TableCell>
                <TableCell className="text-slate-200">{inr(inv.total_inr)}</TableCell>
                <TableCell className="text-slate-400 hidden md:table-cell">{inv.currency_amount?.toLocaleString("en-IN")} {inv.currency}</TableCell>
                <TableCell className="text-slate-500 text-xs hidden sm:table-cell">{inv.issued_at?.slice(0, 10)}</TableCell>
                <TableCell>
                  <Link to={`/invoice/${inv.payment_id}`} target="_blank" data-testid={`invoice-view-${inv.invoice_number}`} className="text-xs text-flame font-semibold hover:underline">View →</Link>
                </TableCell>
              </TableRow>
            ))}
            {!invoices.length && (
              <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-12">No invoices yet.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!active} onOpenChange={() => setActive(null)}>
        <DialogContent className="bg-navy-800 border-white/10 max-w-2xl max-h-[88vh] overflow-y-auto" aria-describedby={undefined}>
          {active && (
            <div data-testid="payment-detail-dialog">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl text-white">{active.customer?.name} — {active.currency_amount?.toLocaleString("en-IN")} {active.currency}</DialogTitle>
              </DialogHeader>
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs font-mono">
                {[["STATUS", effStatus(active).replace(/_/g, " ")], ["SERVICE", `${SERVICE_LABELS[active.service]} · ${active.hours}h`],
                  ["BASE INR", inr(active.total_inr)], [`${active.tax_label || "TAX"} (${active.tax_rate}%)`, inr(active.tax_amount_inr || 0)],
                  ["FX RATE", active.currency !== "INR" ? `1 INR = ${active.fx_rate} ${active.currency}` : "—"],
                  ["FX TIMESTAMP", active.fx_updated_at ? new Date(active.fx_updated_at).toLocaleString("en-IN") : "—"],
                  ["FX PROVIDER", active.fx_provider || "—"], ["ORDER ID", active.razorpay_order_id || "—"],
                  ["PAYMENT ID", active.razorpay_payment_id || "—"], ["EMAIL", active.customer?.email],
                  ["COUNTRY", active.customer?.country || "—"], ["SETTLEMENT", "INR (via Razorpay)"],
                ].map(([k, v]) => (
                  <div key={k} className="rounded-lg bg-navy-900/70 p-3 break-all">
                    <p className="text-[9px] tracking-widest text-slate-500">{k}</p>
                    <p className="mt-1 text-slate-200">{v}</p>
                  </div>
                ))}
              </div>

              {active.refunds?.length > 0 && (
                <div className="mt-5">
                  <p className="font-mono text-[9px] tracking-widest text-slate-500 mb-2">REFUNDS</p>
                  {active.refunds.map((r) => (
                    <div key={r.refund_id} className="rounded-lg bg-navy-900/70 p-3 text-xs font-mono text-slate-300 mb-2">
                      {r.refund_id} · {r.amount?.toLocaleString("en-IN")} {r.currency} · {r.status} · {r.reason || "no reason"} · {r.at?.slice(0, 10)}
                    </div>
                  ))}
                </div>
              )}

              {active.status === "PAID" && !active.refund_status && (
                <div className="mt-6 border-t border-white/10 pt-6">
                  <p className="font-mono text-[9px] tracking-widest text-slate-500 mb-3">INITIATE REFUND (subject to refund policy; timing depends on payment method, Razorpay and banking systems)</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Input type="number" placeholder={`Amount in ${active.currency} (blank = full)`} value={refundAmount} onChange={(e) => setRefundAmount(e.target.value)} data-testid="refund-amount-input" className="bg-navy-900/60 border-white/10" />
                    <Input placeholder="Reason" value={refundReason} onChange={(e) => setRefundReason(e.target.value)} data-testid="refund-reason-input" className="bg-navy-900/60 border-white/10" />
                  </div>
                  <Button onClick={refund} disabled={busy} data-testid="refund-submit-btn" className="mt-4 bg-red-600 hover:bg-red-500 text-white font-heading font-bold">
                    {busy ? "PROCESSING…" : "INITIATE REFUND"}
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
